/**
 * SSG Data Bridge
 *
 * During vite-react-ssg build, onBeforePageRender (vite.config.ts) fetches
 * gym data from Firestore via Admin SDK and stores it on globalThis.
 * This module reads from that global during renderToString, then serialises
 * the data into a <script> tag so the client can hydrate from it instead of
 * waiting for the first Firestore round-trip.
 *
 * On the client, the same getSSGPageData() call reads from the injected
 * <script id="__SSG_DATA__"> tag — guaranteeing a zero-flicker first paint.
 */

import type { Gym } from '../types';

import type { SuburbStyleLink } from './suburbStyleLinks';

export interface SSGPageData {
    /** Discriminates the route type for downstream consumers. */
    type: 'location' | 'style' | 'gym' | 'static';
    /** Pre-fetched gyms for directory routes — ALL of them, not a first-N slice. */
    gyms: Gym[];
    /** Pre-fetched gym for profile routes. */
    gym: Gym | null;
    /** Suburb slug extracted from the route path. */
    locationSlug: string;
    /** Style slug extracted from the route path (style routes only). */
    styleSlug: string | null;
    /**
     * UI-11 (F4) — the BUILT style pages for this suburb, so the page can link to
     * them. Present on `location` routes that host style pages; absent everywhere
     * else, including style routes themselves (a style page does not list its
     * siblings — the operator ruled the row onto suburb pages only).
     *
     * ⚠️ Derived at build time from the ROUTE LIST, never from gym data — see the
     * comment at the injection site in vite.config.ts. That is what makes every
     * emitted link soft-404-safe by construction.
     */
    styleLinks?: SuburbStyleLink[];
    /**
     * The suburb's postcode as RESOLVED BY THE BUILD for this page's title (audit O2).
     *
     * Injected rather than re-derived in the browser, and that is load-bearing: the
     * authoritative source is the official locality register, which the client cannot
     * read. If the browser recomputed it from the gym records it would disagree with
     * the prerendered <title> and sr-only <h1> on every suburb whose records contradict
     * the register (24 of 601 at the time of writing) — a hydration mismatch AND a
     * wrong tab title. One derivation, at build time, carried across.
     */
    titlePostcode?: string;
    /**
     * UI-131 PART D — the suburb's geographic centre, from the official locality
     * register, on `location` and `style` routes. Absent everywhere else.
     *
     * ⚠️ THIS IS NOT COSMETIC AND IT IS NOT A CACHE. Before Part D the ONLY source of
     * `searchCenter` on a cold arrival was the directory's mount reconcile — a background
     * search fired on every prerendered directory view. That reconcile is now gone, so
     * this field is the sole supplier, and `hooks/useDirectoryResults.ts` falls back to
     * the STATE centre without it: "sort by distance" would rank a suburb's gyms by their
     * distance from the middle of the state. Wrong, not stale.
     *
     * ⭐ Identical to what the backend would have returned, BY CONSTRUCTION rather than by
     * comparison: `functions/src/index.ts:74` `resolveCenterByEntryId` reads
     * `locations_au/{version}/states/{STATE}/entries/{id}` and returns that document's
     * `lat`/`lng`; the build reads the same collection and the same two fields. The live
     * search callable is App-Check gated and cannot be called from a build or a test, so a
     * value-for-value comparison is not available — this is the structural proof that
     * stands in its place, and the build refuses to emit a route without a centre.
     *
     * ⭐ DL-1 (`docs/audit/directory-loading-ux-audit.md`, deployed 2026-07-03) is the
     * ruling that created the reconcile, and it names `searchCenter` as one of its two
     * reasons — the other being an SSG payload that was then a 12-gym slice. The payload
     * is no longer sliced (see `gyms` above) and this field discharges the second, so Part
     * D completes DL-1 rather than reversing it. **Do not restore the reconcile without
     * first removing this field — two sources for one value is how they drift.**
     */
    searchCenter?: { lat: number; lng: number } | null;
    /**
     * Post summaries for the blog index and category pages (audit H7). Absent on
     * every other route.
     *
     * Deliberately NOT the full post: `body` is excluded and `readTime` — the only
     * thing the card derived from it — is precomputed at build. Eleven article bodies
     * would several-fold the weight of /blog to render a number.
     *
     * `published_at` is an ISO-8601 string, not a Date: this payload is serialised
     * into the page as JSON, and JSON has no date type. Consumers rehydrate it.
     */
    blogPosts?: SsgBlogPostSummary[];
    /**
     * WHICH listing `blogPosts` was built for: `null` = the /blog index, otherwise the
     * category slug. Present whenever `blogPosts` is.
     *
     * This exists because the payload is NOT scoped to the page you are looking at. The
     * `__SSG_DATA__` tag is injected outside the React root by the hard-loaded page and is
     * never rewritten on client-side navigation, so after a pill click the DOM still holds
     * the PREVIOUS route's payload. Without this field a category page seeded from the
     * /blog payload and rendered other categories' posts under its own heading. The gym
     * routes already guard the identical hazard by matching stableId (App.tsx).
     *
     * Consumers must adopt the seed ONLY when this matches their route.
     */
    blogListingCategory?: string | null;
    /**
     * The full post for a /blog/:category/:slug route (UI-06) — body included,
     * build-sanitized. Absent on every other route. See SsgBlogPostFull for the
     * sanitization and route-scoping contracts.
     */
    blogPost?: SsgBlogPostFull;
}

/**
 * The FULL post for a /blog/:category/:slug route (UI-06). Unlike the listing
 * summaries, `body` is present — one body on its own page is the article
 * itself, so the listing pages' weight argument does not apply.
 *
 * `body` is SANITIZED AT BUILD TIME (vite.config.ts, jsdom-backed DOMPurify
 * running the exact ALLOWED_* exports of lib/sanitizeBlogHtml). That is
 * load-bearing TWICE OVER: during the SSG render sanitizeBlogBody is a
 * pass-through (plain dompurify has no .sanitize in Node), AND the hydrated
 * first paint keeps the baked innerHTML verbatim (React 19 production
 * hydration does not re-apply it) — so the build-time sanitize plus the
 * narrow allowlist are the ONLY protection on the baked path. The browser
 * re-sanitizes only client-side-navigation fetches. See the review-corrected
 * security model in lib/sanitizeBlogHtml.ts before touching any of this.
 *
 * Route scoping mirrors blogListingCategory (see its docblock): the
 * __SSG_DATA__ tag survives client-side navigation, so consumers must adopt
 * this payload ONLY when category AND slug both match their own route.
 */
export interface SsgBlogPostFull extends SsgBlogPostSummary {
    body: string;
    /**
     * Topic tags, carried through the bake so a DIRECTLY-LOADED article can compute its
     * call-to-action target. Absent (not empty) when the post has none — see
     * lib/blogTagPayload.ts, which is the only thing that may populate these.
     * ⚠️ Typed as string[] deliberately: this describes the JSON payload as it is READ
     * BACK, before lib/blogSeed.ts narrows it to the literal unions on HydratedBlogPost.
     */
    styles?: string[] | undefined;
    regions?: string[] | undefined;
    audience?: string[] | undefined;
    ageGroups?: string[] | undefined;
    /** ISO-8601, when the pipeline recorded an update. */
    updated_at?: string | undefined;
    seo_title?: string | undefined;
}

/** One post as the listing pages need it — see SSGPageData.blogPosts. */
export interface SsgBlogPostSummary {
    slug: string;
    category: string;
    title: string;
    excerpt: string;
    /** ISO-8601. */
    published_at: string;
    featured_image_url?: string | undefined;
    /** Precomputed from the body at build time (lib/readTime). */
    readTime: number;
}

const SSG_DATA_ELEMENT_ID = '__SSG_DATA__';

/**
 * Read SSG page data for the current route.
 *
 * - During SSG (Node.js): reads from globalThis.__SSG_PAGE_DATA__
 *   which was set by onBeforePageRender in vite.config.ts.
 * - During client hydration: reads from the injected <script> JSON tag.
 * - Returns null if no data is available (e.g. dev mode or non-SSG paths).
 */
export function getSSGPageData(): SSGPageData | null {
    if (typeof window === 'undefined') {
        // SSG rendering context — read from Node.js global set by the build hook
        return (globalThis as Record<string, unknown>).__SSG_PAGE_DATA__ as SSGPageData ?? null;
    }
    // Client hydration — read from injected <script> tag
    try {
        const el = document.getElementById(SSG_DATA_ELEMENT_ID);
        if (!el || !el.textContent) return null;
        return JSON.parse(el.textContent) as SSGPageData;
    } catch {
        return null;
    }
}
